import React from 'react';
import useTargetStorage from "@/hooks/useTargetStorage"
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Section from 'components/layout/section';
import SectionHeader from 'components/layout/sectionHeader';
import SectionHeaderTitle from 'components/layout/sectionHeaderTitle';
import SectionHeaderActions from 'components/layout/sectionHeaderActions';
import PageTitle from 'features/navigation/pageTitle';
import RightSidebarTransaction from 'components/sidebar/right/RightSidebarTransaction';
import { MainSheetHeaderActions } from '@/components/layout/layoutChrome';
import useTransactions from '@/hooks/useTransactions';
import useStorages from '@/hooks/useStorages';
import useStorageLimits from '@/hooks/useStorageLimits';
import { paramGuardStorage, paramGuardTransaction } from '@/utils/paramGuard';
import { FormStatus, TransactionType, AccessRole, DeviceSize } from '@/types';
import useWindowDimensions from '@/hooks/useWindowDimensions';
import FloatingButtonGroup from '@/components/buttons/floatingButtonGroup';
import FloatingButtonGroupButton from '@/components/buttons/FloatingButtonGroupButton';
import FloatingButtonGroupHr from '@/components/buttons/FloatingButtonGroupHr';
import IconButton from '@/components/buttons/iconButton';
import { ReactComponent as MoreVertIcon } from 'assets/icons/more_vert.svg';
import SectionDivider from '@/components/layout/sectionDivider';
import TableList from '@/components/list/tableList/tableList';
import TableListRow from '@/components/list/tableList/tableListRow';
import TableListCell from '@/components/list/tableList/tableListCell';
import Tag from '@/components/tags/tag';
import ActionsList from '@/features/storages/products/actions/actionList';
import Button from '@/components/inputs/button';
import useLayout from '@/hooks/useLayout';
import { ReactComponent as AddIcon } from '@/assets/icons/add.svg';
import { ReactComponent as HistoryIcon } from 'assets/icons/restore.svg';
import { decodeFirebaseError } from '@/utils/firebaseHelpers';
import useAccess from '@/hooks/useAccess';

const TransactionPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { displayIfSize } = useWindowDimensions();
	const { targetStorageDocId: storageId } = useTargetStorage();
	const { transactionId } = useParams<{ transactionId: string }>();
	const { transaction, transactionStatus, finalizeTransactionAsync, isFinalizing } = useTransactions({ storageId, transactionId });
	const {
		storage,
		storageStatus,
		isCustomersAndSuppliersEnabled,
		isTransactionsEnabled
	} = useStorages({ storageId });
	const { canUseCustomers, canUseSuppliers } = useStorageLimits({ storagePlan: storage?.settings?.storagePlan });
	const showSupplier = canUseSuppliers && isCustomersAndSuppliersEnabled();
	const showCustomer = canUseCustomers && isCustomersAndSuppliersEnabled();
	const { openDialog, addToast } = useLayout();
	const { limitToRoles } = useAccess({ storageId: storageId || '' });
	const canMutateStock = limitToRoles([AccessRole.OWNER, AccessRole.EDITOR, AccessRole.WORKER]);
	const canEdit = !!transaction?.isDraft && !transaction?.isFinalized;

	// Check params and declare safe ones to use in JSX
	if (
		!paramGuardStorage({ storageId, storage, storageStatus, navigate }) ||
		!paramGuardTransaction({ storageId, transactionId, transaction, transactionStatus, navigate })
	) {
		return <></>
	}
	const safeStorageId = storageId!;
	const safeTransactionId = transactionId!;

	// Redirect if transactions are disabled for this storage
	if (!isTransactionsEnabled()) {
		navigate(`/storage`);
		return <></>;
	}

	const handlePublish = async () => {
		if (!safeStorageId || !safeTransactionId) return;
		try {
			await finalizeTransactionAsync({
				storageId: safeStorageId,
				transactionId: safeTransactionId,
			});
			addToast({ text: t('toastTransactionPublished', 'Transaction published'), colorScheme: 'green' });
		} catch (error: unknown) {
			const message = decodeFirebaseError(error instanceof Error ? error.message : String(error));
			addToast({ text: message, colorScheme: 'red' });
		}
	};

	return (
		<>
			<PageTitle title={safeTransactionId ? t(transaction?.type === TransactionType.INCOMING ? 'titleTransactionIncoming_' : 'titleTransactionOutgoing_', { id: transaction?.transactionId }) : t('titleProduct')} />
			<MainSheetHeaderActions>
						{transactionStatus === FormStatus.LOADING ? (
							<Tag colorScheme='gray' className="skeleton"></Tag>
						) : (
							<>
								{transaction!.isDraft === true && transaction!.isFinalized === false ? (
									<>
										<Tag colorScheme='yellow' className="mr-2">{t('labelDraft', 'Draft')}</Tag>
										{canMutateStock && (
										<Button wideOnMobile
											design="primary"
											onClick={handlePublish}
											disabled={isFinalizing}
										>
											{isFinalizing ? t('buttonPublishing', 'Publishing...') : t('buttonPublishTransaction', 'Publish')}
										</Button>
										)}
									</>
								) : (
									<Tag colorScheme='blue'>{t('labelFinalized', 'Published')}</Tag>
								)}
							</>
						)}
						{displayIfSize([DeviceSize.SM]) && transaction &&
							<FloatingButtonGroup id={`transaction_${safeTransactionId}`} openingButton={<IconButton icon={<MoreVertIcon />} />}>
								<FloatingButtonGroupButton onClick={() => openDialog('transaction_history')}>{t('viewHistory')}</FloatingButtonGroupButton>
								{canMutateStock &&
									<>
										{canEdit &&
											<>
												<FloatingButtonGroupButton to={`/storage/transactions/${safeTransactionId}/edit`}>{t('buttonEditTransaction')}</FloatingButtonGroupButton>
												<FloatingButtonGroupHr />
											</>
										}
										<FloatingButtonGroupButton onClick={() => openDialog('delete_transaction', { storageId: safeStorageId, transactionId: safeTransactionId })}>{t('delete')}</FloatingButtonGroupButton>
									</>
								}
							</FloatingButtonGroup>
						}
			</MainSheetHeaderActions>
			<Section>
				<TableList width="fit">
					<TableListRow hoverEffect={false}>
						<TableListCell isLabel>{t('labelDescription')}</TableListCell>
						<TableListCell>{transactionStatus === FormStatus.LOADING ? <p className='skeleton short'></p> : <>{transaction!.description || '-'}</>}</TableListCell>
					</TableListRow>
					{showCustomer && (
						<TableListRow hoverEffect={false}>
							<TableListCell isLabel>{t('labelCustomer')}</TableListCell>
							<TableListCell>{transactionStatus === FormStatus.LOADING ? <p className='skeleton short'></p> : transaction!.customer?.customerName ? (
								<Link to={`/storage/customers/${transaction!.customer?.customerId}`}>{transaction!.customer?.customerName}</Link>
							) : '-'}</TableListCell>
						</TableListRow>
					)}
					{showSupplier && (
						<TableListRow hoverEffect={false}>
							<TableListCell isLabel>{t('labelSupplier')}</TableListCell>
							<TableListCell>{transactionStatus === FormStatus.LOADING ? <p className='skeleton short'></p> : transaction!.supplier?.supplierName ? (
								<Link to={`/storage/suppliers/${transaction!.supplier?.supplierId}`}>{transaction!.supplier?.supplierName}</Link>
							) : '-'}</TableListCell>
						</TableListRow>
					)}
				</TableList>
				<SectionDivider />
				{transactionStatus !== FormStatus.LOADING && transaction && (
					<>
						<SectionHeader>
							<SectionHeaderTitle>
								<h2>{t('titleActions')}</h2>
							</SectionHeaderTitle>
							{transaction.isDraft && !transaction.isFinalized && canMutateStock && (
								<SectionHeaderActions>
									{transaction.type === TransactionType.INCOMING ? (
										// Incoming transactions can only have "add" (increment) actions
										<IconButton
											colorScheme="blue"
											icon={<AddIcon />}
											onClick={() => openDialog(`add_to_stock`, { transactionId: safeTransactionId })}
										/>
									) : (
										// Outgoing transactions can only have "remove" (decrement) actions
										<IconButton
											colorScheme="blue"
											icon={<AddIcon />}
											onClick={() => openDialog(`remove_from_stock`, { transactionId: safeTransactionId })}
										/>
									)}
									{/* Note: Adjust actions cannot be linked to transactions */}
								</SectionHeaderActions>
							)}
					</SectionHeader>
					<ActionsList 
						storageId={safeStorageId} 
						actionPaths={transaction?.actions?.actionPaths}
						transactionId={safeTransactionId}
						displayHelper={false}
						/>
					</>
				)}
			</Section>
			<RightSidebarTransaction storageId={safeStorageId} transactionId={safeTransactionId} />
		</>
	);
};

export default TransactionPage;
